/*
 * Project: bellcloud
 * File: packages/ui/components/common/Layout.tsx
 * Description: Reusable styled layout components
 */

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { media } from '@/theme/tokens/breakpoints';
import type { SpacingTokens } from '@/theme/tokens/spacing';

type SpacingKey = keyof SpacingTokens;

export const Flex = styled('div', {
  shouldForwardProp: prop => prop !== 'wrap' && prop !== 'direction' && prop !== 'align' && prop !== 'justify' && prop !== 'gap' && prop !== 'padding',
})<{
  direction?: 'row' | 'column';
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  gap?: SpacingKey;
  wrap?: boolean;
  padding?: SpacingKey;
}>`
  display: flex;
  flex-direction: ${props => props.direction || 'row'};
  align-items: ${props => {
    switch (props.align) {
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'stretch':
        return 'stretch';
      case 'center':
      default:
        return 'center';
    }
  }};
  justify-content: ${props => {
    switch (props.justify) {
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'between':
        return 'space-between';
      case 'around':
        return 'space-around';
      case 'center':
      default:
        return 'center';
    }
  }};
  gap: ${({ theme, gap }) => (gap ? `${theme.spacing[gap]}px` : '0')};
  ${props => props.wrap && 'flex-wrap: wrap;'}
  padding: ${({ theme, padding }) => (padding ? `${theme.spacing[padding]}px` : '0')};
`;

export const Grid = styled.div<{
  columns?: number;
  gap?: SpacingKey;
  responsive?: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 3}, 1fr);
  gap: ${({ theme, gap }) => (gap ? `${theme.spacing[gap]}px` : `${theme.spacing[4]}px`)};

  ${({ responsive }) =>
    responsive &&
    css`
      ${media.maxSm`grid-template-columns: 1fr;`}
    `}
`;
